import { customAlphabet } from "nanoid";
import { ulidFactory } from "ulid-workers";

const ulid = ulidFactory({
  monotonic: true,
});

const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const charLength = chars.length;
export function randomString(length = 16): string {
  const resultArray = new Array(length);

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charLength);
    resultArray[i] = chars[randomIndex];
  }

  return resultArray.join("");
}

// empty prefix means we don't generate ids for this resource
export const resourcePrefixes = {
  api_key: {
    prefix: "zirp",
    category: "api_keys",
    len: 64,
    example: "GxiGu9h2nkrfbnEhtH4LtyGNUi46mXghgadyeJPiwb38kNtVaRVCvATihhY6VGkZ",
  },
  api_key_token: {
    prefix: "sk",
    category: "api_keys",
    len: 64,
    example: "rMDbfEMzUuFCyM4TMpZoZBuL38wqJ3SJDVBMgcDEAHorWQ9ResRDT8aYKGz1Tz8J",
  },
  project_token: {
    prefix: "zirpt",
    category: "project_tokens",
    len: 64,
    example: "dn6q4VPn74yY5XgW6xw7QqHqLt4FrGx8xUhpcJhJPx5kFmdS13MCsqAEMUU9HdqM",
  },
  user: {
    prefix: "usr",
    category: "users",
    len: 32,
    example: "3fV9YJAfvvyb4mYvuWiJWRjS5gDkQvxX",
  },
  workspace: {
    prefix: "ws",
    category: "workspaces",
    len: 32,
    example: "vor9coGxjaSjZYVWPzLsgXkjDTh5D75E",
  },
  invite: {
    prefix: "invite",
    category: "invites",
    len: 64,
    example: "ZS2E4wVASERMwT14H2CpdGBz6APA7kYTsok1sBNbGfMUvm7nq2nboRAHthaLd3Kz",
  },
  project: {
    prefix: "prj",
    category: "project",
    len: 24,
    example: "fKusuLcXQZij5x7URG98aP2z",
  },
  note: {
    prefix: "note",
    category: "notes",
    len: 24,
    example: "Fo8hgFzB1oTwt3m7SsfzYnGu",
  },
  judge: {
    prefix: "jdg",
    category: "judges",
    len: 24,
    example: "Fo8hgFzB1oTwt3m7SsfzYnGu",
  },
  shot: {
    prefix: "shot",
    category: "shots",
    len: 24,
    example: "fKusuLcXQZij5x7URG98aP2z",
  },
  judgement: {
    prefix: "judgement",
    category: "judgements",
    len: 24,
    example: "fKusuLcXQZij5x7URG98aP2z",
  },
  query: {
    prefix: "qry",
    category: "queries",
    len: 24,
    example: "eXLh9zaVkfzLERZG6pBjwMoj",
  },
  shallow_query: {
    prefix: "sqry",
    category: "shallow_queries",
    len: 24,
    example: "",
  },
  query_run: {
    prefix: "run",
    category: "query_run",
    len: 24,
    example: "ZXeLpRXzUSktvn7nk8kFG2Gh",
  },
  note_version: {
    prefix: "note_ver",
    category: "note_versions",
    len: 32,
    example: "FDXQdU65dK5Afm6Ccsq7wR1cr8cmr2pR",
  },
  judge_version: {
    prefix: "jdg_ver",
    category: "judge_versions",
    len: 32,
    example: "FDXQdU65dK5Afm6Ccsq7wR1cr8cmr2pR",
  },
  session: {
    prefix: "",
    category: "sessions",
    len: 0,
    example: "",
  },
  workspace_member: {
    prefix: "",
    category: "workspace_members",
    len: 0,
    example: "",
  },
  telemetry_group: {
    prefix: "",
    category: "telemetry_group",
    len: 0,
    example: "",
  },
  telemetry_person: {
    prefix: "",
    category: "telemetry_person",
    len: 0,
    example: "",
  },
  telemetry_event: {
    prefix: "",
    category: "telemetry_event",
    len: 0,
    example: "",
  },
  internal_event: {
    prefix: "int_evt",
    category: "internal_events",
    len: 64,
    example: "bkrD5oNCweaWai8tbexvL9R5Vux8r3wEeFGyr3iiphXQjHQQJyS7aY5htyMt5e4G",
  },
} as const;

export type ResourceTypes = keyof typeof resourcePrefixes;

// fyi spelled this out as it's missing lower case l (this letter is usually easy to confuse with 1 when reading / copying)
const alphabet = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
const nanoid = customAlphabet(alphabet);

export function resourceIdRegex(prefix: ResourceTypes): RegExp {
  if (["telemetry_event", "telemetry_person", "telemetry_group"].includes(prefix)) {
    return /[\s\S]*/;
  }
  const val = resourcePrefixes[prefix];
  const pattern = `^${val.prefix}_[${alphabet}]{${val.len}}$`;
  return new RegExp(pattern);
}

export function exampleId(prefix: ResourceTypes) {
  const val = resourcePrefixes[prefix];
  return `${val.prefix}_${val.example}`;
}

export function newId(prefix: ResourceTypes): string {
  const val = resourcePrefixes[prefix];
  return [val.prefix, nanoid(val.len)].join("_");
}

export function getResourceCategory(prefix: ResourceTypes) {
  const val = resourcePrefixes[prefix];
  return val.category;
}

export function generateInsertId(timestamp: number | string) {
  if (typeof timestamp === "string") {
    return ulid(new Date(timestamp).getTime());
  }
  return ulid(timestamp);
}
